import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit2, Key, XCircle } from "lucide-react";
import logger from "@/lib/logger";

export default function UserManagementTab() {
  const [allUsers, setAllUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const { user } = useAuth();

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setAllUsers(data.users || []);
    } catch (err) { logger.error("Failed to fetch users:", err); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) { toast.error("Email and password required"); return; }
    try {
      await api.post("/admin/users", newUser);
      toast.success("User created");
      setShowCreateUser(false);
      setNewUser({});
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      await api.put(`/admin/users/${editingUser.id}`, {
        name: editingUser.name,
        role: editingUser.role,
        default_location: editingUser.default_location,
        is_active: editingUser.is_active,
      });
      toast.success("User updated");
      setEditingUser(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleResetPassword = async () => {
    if (!resetPwUser || newPassword.length < 6) { toast.error("Min 6 characters"); return; }
    try {
      await api.put(`/admin/users/${resetPwUser.id}/reset-password`, { password: newPassword });
      toast.success("Password reset");
      setResetPwUser(null);
      setNewPassword("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm("Deactivate this user?")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("User deactivated");
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <Card className="mt-4 p-5" data-testid="user-management-panel">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
          <div>
            <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>User Management</h3>
            <p className="text-xs text-gray-500">Create, edit, and manage user accounts and roles</p>
          </div>
          <Button onClick={() => { setNewUser({ role: "user", default_location: "Focal Point" }); setShowCreateUser(true); }} className="bg-[#1B3C87] h-9 text-xs" data-testid="create-user-btn">
            <Plus size={14} className="mr-1" /> Create User
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#1B3C87]/10">
                <TableHead className="text-xs uppercase font-bold">Name</TableHead>
                <TableHead className="text-xs uppercase font-bold">Email</TableHead>
                <TableHead className="text-xs uppercase font-bold">Role</TableHead>
                <TableHead className="text-xs uppercase font-bold">Location</TableHead>
                <TableHead className="text-xs uppercase font-bold">Status</TableHead>
                <TableHead className="text-xs uppercase font-bold w-48">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allUsers.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No users found</TableCell></TableRow>
              )}
              {allUsers.map(u => (
                <TableRow key={u.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium text-sm">{u.name || "—"}</TableCell>
                  <TableCell className="text-xs">{u.email}</TableCell>
                  <TableCell>
                    <Badge className={{"admin": "bg-[#E31E24]", "manager": "bg-[#1B3C87]"}[u.role] || "bg-gray-500"}>
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{u.default_location || "—"}</TableCell>
                  <TableCell>
                    <Badge className={u.is_active !== false ? "bg-emerald-600" : "bg-gray-400"}>
                      {u.is_active !== false ? "Active" : "Disabled"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setEditingUser({ ...u })} data-testid={`edit-user-${u.id}`}>
                        <Edit2 size={10} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => { setResetPwUser(u); setNewPassword(""); }} data-testid={`reset-pw-${u.id}`}>
                        <Key size={10} className="mr-1" /> Reset PW
                      </Button>
                      {u.email !== user?.email && u.is_active !== false && (
                        <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeactivateUser(u.id)} data-testid={`deactivate-${u.id}`}>
                          <XCircle size={10} className="mr-1" /> Disable
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="max-w-md" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Chivo" }}>Create New User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Name *</Label>
              <Input value={newUser.name || ""} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} className="h-11" data-testid="new-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Email *</Label>
              <Input value={newUser.email || ""} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} className="h-11" data-testid="new-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Password *</Label>
              <Input type="password" value={newUser.password || ""} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} className="h-11" data-testid="new-user-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Role</Label>
              <Select value={newUser.role || "user"} onValueChange={v => setNewUser(p => ({ ...p, role: v }))}>
                <SelectTrigger className="h-11" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User (Guard)</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Default Location</Label>
              <Select value={newUser.default_location || "Focal Point"} onValueChange={v => setNewUser(p => ({ ...p, default_location: v }))}>
                <SelectTrigger className="h-11" data-testid="new-user-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Focal Point">Focal Point</SelectItem>
                  <SelectItem value="Sahnewal">Sahnewal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} className="bg-[#1B3C87]" data-testid="save-user-btn">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="max-w-md" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Chivo" }}>Edit User</DialogTitle></DialogHeader>
          {editingUser && (
            <div className="space-y-3">
              <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">{editingUser.email}</div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Name</Label>
                <Input value={editingUser.name || ""} onChange={e => setEditingUser(p => ({ ...p, name: e.target.value }))} className="h-11" data-testid="edit-user-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Role</Label>
                <Select value={editingUser.role || "user"} onValueChange={v => setEditingUser(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="h-11" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User (Guard)</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Default Location</Label>
                <Select value={editingUser.default_location || "Focal Point"} onValueChange={v => setEditingUser(p => ({ ...p, default_location: v }))}>
                  <SelectTrigger className="h-11" data-testid="edit-user-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Focal Point">Focal Point</SelectItem>
                    <SelectItem value="Sahnewal">Sahnewal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Status</Label>
                <Select value={editingUser.is_active !== false ? "active" : "disabled"} onValueChange={v => setEditingUser(p => ({ ...p, is_active: v === "active" }))}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={handleUpdateUser} className="bg-[#1B3C87]" data-testid="update-user-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPwUser} onOpenChange={() => setResetPwUser(null)}>
        <DialogContent className="max-w-sm" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Chivo" }}>Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {resetPwUser && <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">{resetPwUser.email}</div>}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">New Password *</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="h-11" placeholder="Min 6 characters" data-testid="new-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwUser(null)}>Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-[#E31E24]" data-testid="confirm-reset-pw-btn">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
